<template>
  <MediaVideo
    v-if="activeMedia?.__typename === 'ParagraphPdpStageVideo'"
    :poster="activeMedia.media?.poster"
    :video="activeMedia.media?.video"
    autoplay
    loop
    class="flex h-full w-full flex-col items-center justify-center"
    video-class="object-cover"
    :show-sound-button="activeMedia.sound"
  />
  <MediaImage
    v-else-if="activeMedia?.__typename === 'ParagraphPdpStageImage'"
    v-bind="activeMedia.media"
    :img-class="mainImageClasses"
    class="img-zoom-in flex h-full w-full flex-col items-center justify-center"
    :image-style="imageStylesDesktopMainImage[activeImageIndex]"
  />
  <MediaImage
    v-else-if="image"
    :img-class="mainImageClasses"
    :url="image"
    :alt="imageAlt"
    class="smaller-main-image img-zoom-in flex h-full w-full flex-col items-center justify-center"
    :image-style="imageStyleDesktopAlternativeMainImage"
  />
</template>

<script setup lang="ts">
import type {
  PageProductVariationGiftFragment,
  PageProductVariationGiveawayFragment,
  PageProductVariationStrapFragment,
  PageProductVariationToolFragment,
  PageProductVariationWatchFragment,
  ParagraphPdpStageImageFragment,
  ParagraphPdpStageVideoFragment,
} from '#build/graphql-operations'

const props = defineProps<{
  images: (ParagraphPdpStageImageFragment | ParagraphPdpStageVideoFragment)[]
  activeImageIndex: number
  variation:
    | PageProductVariationGiftFragment
    | PageProductVariationGiveawayFragment
    | PageProductVariationToolFragment
    | PageProductVariationWatchFragment
    | PageProductVariationStrapFragment
}>()

let imageAlt = ''
const image = computed(() => {
  const variation = props.variation

  let imageUrl
  if ('fieldImageVertical' in variation && variation.fieldImageVertical) {
    imageUrl = variation.fieldImageVertical
    if (variation.title) {
      imageAlt = 'Vertical image of ' + variation.title
    }
  } else if ('image' in variation && variation.image) {
    imageUrl = variation.image
    if (variation.title) {
      imageAlt = 'Image of ' + variation.title
    }
  }

  return imageUrl
})

const activeMedia = computed(() => {
  if (props.images.length > props.activeImageIndex) {
    return props.images[props.activeImageIndex]
  }
})

const smallerMainImage = computed(() => props.images.length === 0)
const mainImageClasses = computed(() =>
  smallerMainImage.value
    ? 'max-h-[80%] object-scale-down'
    : 'object-cover h-full w-full',
)

const imageStylesDesktopMainImage = computed(() => {
  return props.images.map(
    (
      mediaFragement:
        | ParagraphPdpStageVideoFragment
        | ParagraphPdpStageImageFragment,
    ) => {
      const image =
        mediaFragement.__typename === 'ParagraphPdpStageImage'
          ? mediaFragement.media?.image
          : undefined
      const imageWidth = image?.width
      const imageHeight = image?.height
      const imageAspectRatio =
        imageWidth && imageHeight ? imageWidth / imageHeight : undefined
      const imageAspectRatioRelativToWindow =
        imageWidth && imageHeight
          ? (imageWidth * 2) / imageHeight // width * 2 since we are only display 50% of vw.
          : undefined

      if (
        !imageAspectRatio ||
        !imageAspectRatioRelativToWindow ||
        !imageHeight
      ) {
        return undefined
      }

      return defineImageStyleOris({
        type: 'pictures',
        pictures: [
          // width 0 - 1200
          {
            media: `(max-width: 1200px) and (min-aspect-ratio: ${imageAspectRatioRelativToWindow})`,
            width: 600,
          },
          // width 1200 - max (2500)
          {
            media: `(min-aspect-ratio: ${imageAspectRatioRelativToWindow})`,
            width: 1250,
          },
          // height 0 - 700
          {
            media: `(max-height: 700px) and (max-aspect-ratio: ${imageAspectRatioRelativToWindow})`,
            height: 700,
            aspectRatio: imageAspectRatio,
          },
          // height 700 - 1400
          {
            media: `(max-height: 1400px) and (max-aspect-ratio: ${imageAspectRatioRelativToWindow})`,
            height: 1400,
            aspectRatio: imageAspectRatio,
          },
          // height 1400 - max (and default)
          {
            viewport: 'fallback',
            height: imageHeight,
            aspectRatio: imageAspectRatio,
          },
        ],
      })
    },
  )
})

const imageStyleDesktopAlternativeMainImage = defineImageStyleOris({
  type: 'scale',
  pictures: {
    sm: {
      widthScale: 1,
    },
    lg: {
      widthScale: 0.5,
    },
  },
})
</script>
